import React from "react";

export const presaleContent = {
  create_presale: {
    title: "Create a Presale",
    content: (
      <>
        <h3>How to Launch a Presale on TrendPad</h3>
        <h4>Overview</h4>
        <p>
          Connect your wallet and navigate to the presale creation page. Verify
          your token address or create a new token for the launchpad. Configure
          essential presale parameters like rates, funding goals, listing
          options, and timings, ensuring all constraints are met. Add required
          project details, including your logo and website. Review all
          information carefully and submit the transaction to create your
          presale pool.
        </p>

        <h4>Step 1: Verify Your Token</h4>
        <ol>
          <li>Connect your cryptocurrency wallet (e.g., MetaMask).</li>
          <li>
            Go to the TrendPad homepage, click on <strong>Launchpads</strong>{" "}
            &gt; <strong>Create Presale</strong>.
          </li>
          <li>
            Enter your existing token address. If you don't have one, you can
            create a new token.
          </li>
          <li>
            Choose the currency you want to raise funds in (e.g., ETH, BNB).
          </li>
          <li>Select your preferred fee option.</li>
          <li>
            <strong>Choose your listing option:</strong>
            <ul>
              <li>
                <strong>Auto Listing:</strong> Liquidity will be added
                automatically by TrendPad after you finalize the presale.
              </li>
              <li>
                <strong>Manual Listing:</strong> You will need to add liquidity
                to the decentralized exchange (DEX) manually yourself.
              </li>
            </ul>
          </li>
          <li>Enable Affiliate program if desired.</li>
          <li>Review your selections on this page and click Next.</li>
        </ol>
        <img
          src="/docs/presale/3.png"
          alt="Presale creation step 1"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />

        <h4>Step 2: DeFi Launchpad Info</h4>
        <p>Enter the specific details for your presale.</p>
        <ul>
          <li>
            <strong>Presale Rate:</strong> Set how many tokens contributors will
            receive for 1 unit of ETH/BNB. Must be a positive number.
          </li>
          <li>
            <strong>Whitelist:</strong> Choose 'Enable' if you want only
            whitelisted addresses to contribute initially. You can enable or
            disable this later.
          </li>
          <li>
            <strong>Soft Cap:</strong> Minimum funds to raise. Must be &ge; 25%
            of Hard Cap.
          </li>
          <li>
            <strong>Hard Cap:</strong> Maximum funds to raise.
          </li>
          <li>
            <strong>Min Buy & Max Buy:</strong> Limits per wallet.
          </li>
          <li>
            <strong>Refund Type:</strong>
            <ul>
              <li>
                <strong>Refund:</strong> Contributors can claim funds back if
                Soft Cap not met.
              </li>
              <li>
                <strong>Burn:</strong> Unsold tokens are sent to a dead address.
              </li>
            </ul>
          </li>
          <li>
            <strong>Router:</strong> Select the DEX router (e.g., Uniswap,
            PancakeSwap).
          </li>
          <li>
            <strong>Liquidity (%):</strong> Percentage of raised funds going to
            DEX liquidity (51% - 100%).
          </li>
          <li>
            <strong>Listing Rate:</strong> Initial price on DEX. Usually lower
            than Presale Rate to incentivize presale buyers.
          </li>
          <li>
            <strong>Start Time & End Time:</strong> Duration of the presale.
          </li>
          <li>
            <strong>Liquidity Lockup (days):</strong> Duration to lock the DEX
            liquidity (e.g., 30 days).
          </li>
          <li>
            <strong>Vesting:</strong> Optionally set up token vesting for
            contributors.
          </li>
        </ul>
        <img
          src="/docs/presale/7.png"
          alt="DeFi Launchpad Info"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />
        <h4>Step 3: Add Additional Information</h4>
        <p>Provide details about your project.</p>
        <ul>
          <li>
            <strong>Logo URL:</strong> Direct link to your logo (png, jpg, gif).
            Required.
          </li>
          <li>
            <strong>Website:</strong> Project's official website. Required.
          </li>
          <li>
            <strong>Social Media:</strong> Links to Twitter, Telegram, Discord,
            etc.
          </li>
        </ul>

        <h4>Step 4: Finish and Submit</h4>
        <img
          src="/docs/presale/10.png"
          alt="Finish and submit presale"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />
        <ol>
          <li>Carefully check all details.</li>
          <li>
            Click <strong>Approve Spending Token</strong> to allow the contract
            to use your tokens.
          </li>
          <li>
            Click <strong>Confirm and create Launchpad</strong>.
          </li>
          <li>Confirm the transaction in your wallet.</li>
        </ol>

        <h4>Q&A</h4>
        <ul>
          <li>
            <strong>Q: Difference between Auto and Manual Listing?</strong>
            <br />
            A: Auto Listing creates the liquidity pool automatically. Manual
            requires you to add it yourself.
          </li>
          <li>
            <strong>Q: Soft Cap vs Hard Cap?</strong>
            <br />
            A: Soft Cap is the minimum needed for success. Hard Cap is the
            maximum allowed raise.
          </li>
          <li>
            <strong>Q: Can I change whitelist settings later?</strong>
            <br />
            A: Yes, you can enable/disable it anytime.
          </li>
        </ul>

        <h4>Troubleshooting</h4>
        <ul>
          <li>
            <strong>Cannot proceed past Info screen?</strong> Ensure Logo and
            Website URLs are filled.
          </li>
          <li>
            <strong>Transaction fails?</strong> Check gas funds, network
            congestion, and ensure Token Approval was successful.
          </li>
        </ul>
      </>
    ),
  },
  update_presale: {
    title: "Update a Presale",
    content: (
      <>
        <h4>Overview</h4>
        <p>
          Locate your created presale pool via your TrendPad dashboard or a
          direct link.
        </p>

        <h4>Steps</h4>
        <ol>
          <li>
            <strong>Find Your Presale Pool:</strong> Go to Launchpad List or My
            Contributions. Locate your pool and click View.
          </li>
          <li>
            Click
            <strong>Update Pool Info</strong>
          </li>
          <img
            src="/docs/presale/12.png"
            alt="Update Pool Info button"
            style={{
              maxWidth: "100%",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
          <li>
            <strong>Edit Information:</strong>
            <ul>
              <li>Logo URL (Required, must be image URL)</li>
              <li>Website (Required)</li>
              <li>Social Links (Facebook, Twitter, Telegram, etc.)</li>
              <li>Pool Description</li>
            </ul>
          </li>
          <li>
            Click
            <strong>Update:</strong> Click Update and confirm the transaction.
          </li>
          <img
            src="/docs/presale/14.png"
            alt="Update pool information form"
            style={{
              maxWidth: "100%",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
        </ol>

        <h4>Q&A</h4>
        <ul>
          <li>
            <strong>Q: Can I change financial details (rates, caps)?</strong>
            <br />
            A: No, these are locked to protect investors. Only informational
            fields can be updated.
          </li>
          <li>
            <strong>Q: Why is there a gas fee?</strong>
            <br />
            A: Updates are recorded on-chain or require interaction with the
            smart contract.
          </li>
        </ul>

        <h4>Troubleshooting</h4>
        <ul>
          <li>
            <strong>Edit icon missing?</strong> Ensure you are connected with
            the wallet that created the pool.
          </li>
          <li>
            <strong>Transaction failed?</strong> Check gas fees and ensure
            Logo/Website URLs are valid.
          </li>
        </ul>
      </>
    ),
  },
  finalize_presale: {
    title: "Finalize a Presale",
    content: (
      <>
        <h4>How to Finalize a Presale on TrendPad</h4>
        <h4>Overview</h4>
        <p>
          Finalization is possible after your presale ends and meets its Soft
          Cap, or if it reaches the Hard Cap.
        </p>
        <p>
          <strong>Important:</strong> If your token has special transfer rules
          (fees/taxes, max tx limits), you <strong>MUST exclude</strong> the
          TrendPad presale address from these rules before finalizing. Failed to
          do so will cause the transaction to revert.
        </p>

        <h4>Steps</h4>
        <ol>
          <li>
            <strong>Find Token and Pool Address:</strong> Copy the Token Address
            and Pool Address from the presale page.
          </li>
          <li>
            <strong>Exclude Presale from Fees:</strong>
            <ul>
              <li>
                Go to your token contract on the block explorer (e.g.,
                Etherscan/BscScan).
              </li>
              <li>Connect your wallet (must be token owner).</li>
              <li>
                Find functions like <code>excludeFromFees</code>,{" "}
                <code>setExcludedFromRewards</code>, or similar.
              </li>
              <li>
                Enter the <strong>TrendPad Presale Address</strong> and set to
                True/Excluded.
              </li>
              <li>Write the transaction.</li>
            </ul>
          </li>
          <li>
            <strong>Finalize on TrendPad:</strong>
            <ul>
              <li>Return to the Presale View page.</li>
              <li>
                In the Owner Zone, click <strong>Finalize</strong>.
              </li>
              <img
                src="/docs/presale/15.png"
                alt="Finalize button in Owner Zone"
                style={{
                  maxWidth: "100%",
                  marginTop: "16px",
                  marginBottom: "16px",
                }}
              />
              <li>
                Choose the Claim Time and save settings for saving the time
              </li>
              <img
                src="/docs/presale/16.png"
                alt="Set claim time"
                style={{
                  maxWidth: "100%",
                  marginTop: "16px",
                  marginBottom: "16px",
                }}
              />
              <li>
                Click the finalize button for Confirming the transaction. This
                will list the token on the DEX and lock liquidity.
              </li>
              <img
                src="/docs/presale/17.png"
                alt="Finalize confirmation"
                style={{
                  maxWidth: "100%",
                  marginTop: "16px",
                  marginBottom: "16px",
                }}
              />
            </ul>
          </li>
        </ol>

        <h4>Q&A</h4>
        <ul>
          <li>
            <strong>Q: When can I finalize?</strong>
            <br />
            A: When Hard Cap is reached or (End Time passed AND Soft Cap met).
          </li>
          <li>
            <strong>Q: What happens on finalize?</strong>
            <br />
            A: Tokens are distributed to contributors, liquidity is added to DEX
            and locked, and raised funds (minus liquidity portion) are sent to
            you.
          </li>
        </ul>

        <h4>Troubleshooting</h4>
        <ul>
          <li>
            <strong>Finalize button greyed out?</strong> Presale might still be
            active or Soft Cap not met.
          </li>
          <li>
            <strong>Transaction fails?</strong> Most likely caused by Token
            Fees/Taxes. Ensure the Presale Address is excluded from fees in your
            token contract.
          </li>
        </ul>
      </>
    ),
  },
  cancel_presale: {
    title: "Cancel a Presale",
    content: (
      <>
        <p>
          A presale can be cancelled by the owner <em>before it starts</em> or
          if soft cap is not reached by end time.
        </p>
        <p>
          If cancelled, all investors can claim a refund of their contributed
          funds.
        </p>
      </>
    ),
  },
  add_remove_whitelists: {
    title: "Add/Remove Whitelists",
    content: (
      <>
        <p>
          If your presale is set to "Whitelist Only", you can manage the list of
          allowed addresses.
        </p>

        <h4>Steps to Add Users to Whitelist</h4>
        <ol>
          <li>
            <strong>Step 1: Click "Add users to the whitelist" button</strong>
            <br />
            In the "Owner Zone" of your pool page, locate and click the button
            to add users.
          </li>
        </ol>
        <img
          src="/docs/presale/18.png"
          alt="Add users to whitelist button"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />

        <ol start="2">
          <li>
            <strong>Step 2: Paste wallet addresses and save</strong>
            <br />A modal will appear. Paste the wallet addresses of the users
            you want to whitelist (one per line or comma-separated), then click
            Save.
          </li>
        </ol>
        <img
          src="/docs/presale/20.png"
          alt="Add wallet addresses modal"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />
      </>
    ),
  },
  whitelist_tiers: {
    title: "Whitelist Tiers",
    content: (
      <>
        <p>
          TrendPad supports tiered whitelisting (if enabled). This allows
          different groups of users to buy at different times or with different
          allocations.
        </p>
      </>
    ),
  },
  team_vesting: {
    title: "Team Vesting",
    content: (
      <>
        <p>
          Locking team tokens builds trust. You can use our{" "}
          <strong>TrendLock</strong> service to lock team tokens for a specific
          duration.
        </p>
        <p>Vesting schedules can be linear (drip) or cliff-based.</p>
      </>
    ),
  },
  presale_vesting: {
    title: "Presale Vesting",
    content: (
      <>
        <p>
          You can enable vesting for presale investors to prevent immediate
          dumping at launch.
        </p>
        <p>
          Configure the <strong>First Release for Presale</strong> (e.g., 40% at
          TGE) and the vesting cycle (e.g., 10% every month) during pool
          creation.
        </p>
      </>
    ),
  },
};
